import type { IncomingMessage, ServerResponse } from 'http';
import { Result } from '@commercetools/frontend-domain-types/product/Result';
import { serverSession, ServerOptions } from '@commercetools/frontend-sdk';
import { AcceptedQueryTypes } from '@commercetools/frontend-sdk/lib/types/Query';
import { sdk } from 'sdk';
import { PageDataResponse, PagePreviewDataResponse, RedirectResponse, PageFolderStructureResponse } from '../types';
import { GetAccountResult } from 'frontastic/hooks/useAccount/types';
import { Account } from '@commercetools/frontend-domain-types/account';
import { Address } from 'types/account';
import { getSession } from 'next-auth/react';

interface AccountExtended extends Account {
  loggedIn?: boolean;
}

export const getRouteData =
  () =>
  async (
    slug: string[],
    query?: AcceptedQueryTypes,
    req?: IncomingMessage,
    res?: ServerResponse,
  ): Promise<RedirectResponse | PageDataResponse> => {
    const pageSlug = (slug as string[])?.join('/') || '';
    const path = `/${pageSlug !== 'index' ? pageSlug : ''}`;
    const serverOptions: ServerOptions = {
      req: req,
      res: res,
    };

    const response = await sdk.page.getPage({
      path,
      query,
      serverOptions: req && res ? serverOptions : undefined,
    });

    return (response.isError ? {} : response.data) as RedirectResponse | PageDataResponse;
  };

export const getPreview =
  () =>
  async (previewId: string): Promise<PagePreviewDataResponse> => {
    const res = await sdk.page.getPreview({ previewId });

    return (res.isError ? {} : res.data) as PagePreviewDataResponse;
  };

export const getAccount =
  () =>
  async (req?: IncomingMessage, res?: ServerResponse): Promise<GetAccountResult> => {
    const extensions = sdk.composableCommerce;

    const serverOptions: ServerOptions = {
      req: req,
      res: res,
    };

    const response = await extensions.account.getAccount({ serverOptions: serverOptions });

    return (response.isError ? [] : response.data) as unknown as GetAccountResult;
  };

export const getCustomerGroup =
  () =>
  async (req?: IncomingMessage, res?: ServerResponse): Promise<Account> => {
    const serverOptions: ServerOptions = {
      req: req,
      res: res,
    };

    const response = await sdk.callAction<Account>({
      actionName: 'account/getCustomerGroup',
      serverOptions: serverOptions,
    });

    return (response.isError ? [] : response.data) as unknown as Account;
  };

export const getAccountByCustomerEmail =
  () =>
  async (req?: IncomingMessage, res?: ServerResponse): Promise<AccountExtended> => {
    const session = await getSession({ req });
    const serverOptions: ServerOptions = {
      req: req,
      res: res,
    };

    const response = await sdk.callAction<Account>({
      actionName: 'account/getAccountByCustomerEmail',
      serverOptions: serverOptions,
      payload: {
        email: session?.user?.email,
      },
    });

    return (response.isError ? [] : response.data) as unknown as AccountExtended;
  };

export const getCategories =
  () =>
  async (req?: IncomingMessage, res?: ServerResponse): Promise<Result> => {
    const extensions = sdk.composableCommerce;

    const serverOptions: ServerOptions = {
      req: req,
      res: res,
    };

    const response = await extensions.product.queryCategories({ limit: 99 }, { serverOptions: serverOptions });

    return (response.isError ? [] : response.data) as Result;
  };

export const getStructure =
  () =>
  async (path: string, depth: number): Promise<PageFolderStructureResponse> => {
    const res = await sdk.page.getPages({ path, depth });

    return (res.isError ? {} : res.data) as PageFolderStructureResponse;
  };

export const getHeadquartersAddress =
  () =>
  async (req?: IncomingMessage, res?: ServerResponse): Promise<Address> => {
    const serverOptions: ServerOptions = {
      req: req,
      res: res,
    };

    const response = await sdk.callAction<Address>({
      actionName: 'account/getHeadquartersAddress',
      serverOptions: serverOptions,
    });

    return (response.isError ? [] : response.data) as unknown as Address;
  };
